import { __awaiter } from "tslib";
import { useAemConfig } from '@as-react/common-components';
import { authQueries } from '@as-react/common-queries';
import { useQuery } from '@tanstack/react-query';
import { useFeatureToggle } from './useFeatureToggle';
import { useMemo } from 'react';
import { queryClient } from '@as-react/common-queries';
import { EventUtil, localStorageUtil } from '@as-react/common-utils';
import { AUTH_STORAGE_KEY, AUTH_TRIGGER_POST_LOGIN, EventEnum } from '@as-react/common-types';
import { useWishlist } from './useWishlist';
import { useDispatch } from 'react-redux';
import { basketActions } from '../redux/basket/actions/BasketActions.creators';
const AUTH_LOGIN_ENDPOINT = '/auth/login';
export const useOAuth = ({ returnTo, flow } = {}) => {
    const { servicesEndpoint, defaultRequestParams, rootWithLanguageSuffix, oauth, canonicalUrl, pageUrls } = useAemConfig();
    const currentUrl = typeof window !== 'undefined' && window.location ? window.location.href : canonicalUrl;
    const currentPath = typeof window !== 'undefined' && window.location ? window.location.pathname : '';
    const redirectUri = new URL(`${rootWithLanguageSuffix}/auth.html`, currentUrl);
    const { wishlistLogout, wishlistLogin } = useWishlist();
    const dispatch = useDispatch();
    const isNewAuth = useFeatureToggle('use_new_auth_flow');
    const { getUserinfo, logout: logoutQuery } = authQueries({ servicesEndpoint, defaultRequestParams });
    const { data: userInfo } = useQuery(getUserinfo({
        enabled: isNewAuth,
    }));
    const authUrl = useMemo(() => {
        const authUrl = new URL(`${AUTH_LOGIN_ENDPOINT}`, servicesEndpoint.startsWith('http') ? servicesEndpoint : currentUrl);
        const params = new URLSearchParams(authUrl.search);
        params.append('response_type', 'code');
        params.append('client_id', oauth.clientId);
        params.append('scope', 'openid');
        params.append('redirect_uri', redirectUri.toString());
        params.append('returnTo', returnTo || pageUrls.finishRegistration || currentPath);
        if (flow) {
            params.append('flow', flow);
        }
        for (const [key, value] of Object.entries(defaultRequestParams)) {
            if (value !== undefined && value !== null) {
                params.append(key, value.toString());
            }
        }
        authUrl.search = params.toString();
        return authUrl.toString();
    }, [returnTo, flow]);
    const logout = () => __awaiter(void 0, void 0, void 0, function* () {
        const { id_token } = localStorageUtil.getWithExpiry(AUTH_STORAGE_KEY);
        localStorage.removeItem(AUTH_STORAGE_KEY);
        try {
            yield queryClient.fetchQuery(logoutQuery({
                clientId: oauth.clientId,
                redirectUri: currentUrl,
                oidcToken: id_token,
            }, {
                staleTime: 0,
            }));
            yield wishlistLogout();
        }
        finally {
            window.location.reload();
        }
    });
    const postLogin = () => {
        if (sessionStorage.getItem(AUTH_TRIGGER_POST_LOGIN) === 'true') {
            sessionStorage.removeItem(AUTH_TRIGGER_POST_LOGIN);
            wishlistLogin();
            dispatch(basketActions.createGetBasicAction());
            EventUtil.dispatch(EventEnum.USER_LOGGED_IN, { loggedIn: true });
        }
    };
    return {
        authUrl,
        loggedIn: !!(userInfo === null || userInfo === void 0 ? void 0 : userInfo.email),
        logout,
        postLogin,
    };
};
